'use client';

import { Delete, ExpandLess, ExpandMore } from '@mui/icons-material';
import { Box, Collapse, Paper } from '@mui/material';
import {
	DataGridPremium as DataGrid,
	GridActionsCellItem,
	GridApi,
	GridCellParams,
	GridColDef,
	useGridApiRef,
} from '@mui/x-data-grid-premium';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useMutation, useQuery } from '@parkchirp/api-client';
import { adaptGridValueCallback } from '@parkchirp/ui-library';
import type { Dayjs } from 'dayjs';
import React, { FC, MutableRefObject, useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { CREATE_CONVERSION_RIGHT, DELETE_CONVERSION_RIGHT, UPDATE_CONVERSION_RIGHT } from '../../../mutations';
import { GET_RATES_BY_SUBSCRIPTION, GET_SUBLOTS } from '../../../queries';
import { SetShowDialog, SetShowSnackbar } from '../../../types';
import { ConversionRightType, LeasingRight } from '../../../types/leases';
import {
	dayjs,
	getAllowRowEdits,
	getGridRows,
	getTableLatestEndDate,
	leasingRightErrorMessage,
	stopAllEdits,
	validateConversionRightDates,
} from '../../../utils';
import { ClickablePaper } from '../../clickablePaper';
import { SimpleModal } from '../../modals';
import { ControlledTooltip } from '../../toolbar';
import { getRateAndSublotFields, getTableButtons } from './leaseDetails';

type ConversionRightProps = {
	conversionRight: ConversionRightType;
	leasingRight: LeasingRight;
	refetchLeasingRight: () => Promise<void>;
	countAndRateId: number | string;
	handleCancelNewMode: () => void;
	editMode: boolean;
	setEditMode: (x: boolean) => void;
	pageEditInProgress: boolean;
	setShowDialog: SetShowDialog;
	setShowSnackbar: SetShowSnackbar;
};

const getColumns: (
	gridApiRef: MutableRefObject<GridApi>,
	conversionRight: ConversionRightType,
	facilityTimezone: string,
	editMode: boolean,
	newMode: boolean,
	subscriptionHasInvoice: boolean,
) => GridColDef[] = (gridApiRef, conversionRight, facilityTimezone, editMode, newMode, subscriptionHasInvoice) => [
	{
		field: 'actions',
		type: 'actions' as const,
		getActions: ({ id, row }) => {
			const allowRowEdits = getAllowRowEdits(
				row,
				conversionRight.history,
				facilityTimezone,
				newMode,
				subscriptionHasInvoice,
			);
			if (!allowRowEdits) return [];

			if (getGridRows(gridApiRef).length === 1) return [];

			return [
				<GridActionsCellItem
					icon={<Delete />}
					onClick={() => gridApiRef.current.updateRows([{ id, _action: 'delete' }])}
					label=''
					key='delete'
				/>,
			];
		},
		flex: 0.5,
		cellClassName: 'MuiDataGrid-cell--actions',
	},
	{
		field: 'startDate',
		headerName: 'Start Date',
		headerAlign: 'center' as const,
		align: 'center' as const,
		sortable: false,
		editable: true,
		flex: 1,
		valueFormatter: adaptGridValueCallback(({ value }) => dayjs.utc(value).format('MM/DD/YYYY')),
		renderEditCell: ({ id, field, value }) => (
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<DatePicker<Dayjs>
					value={dayjs(value)}
					onChange={(newValue) => {
						const formattedValue = newValue.format('YYYY-MM-DD');
						gridApiRef.current.setEditCellValue({ id, field, value: formattedValue });
					}}
					slotProps={{ openPickerIcon: { 'data-cy': `start-date-picker` } }}
					sx={{
						'& .MuiOutlinedInput-input': { fontSize: 'small' },
						'& .MuiOutlinedInput-notchedOutline': {
							border: 'none',
						},
					}}
				/>
			</LocalizationProvider>
		),
	},
	{
		field: 'endDate',
		headerName: 'End Date',
		headerAlign: 'center' as const,
		align: 'center' as const,
		sortable: false,
		flex: 1,
		valueFormatter: adaptGridValueCallback(({ value }) => (value ? dayjs.utc(value).format('MM/DD/YYYY') : '-')),
		renderCell: ({ value }) => (
			<ControlledTooltip
				title='End date is automatically added upon save'
				placement='top'
				arrow
				disabled={!editMode}
			>
				<Box>{value ? dayjs.utc(value).format('MM/DD/YYYY') : '-'}</Box>
			</ControlledTooltip>
		),
	},
	{
		field: 'conversionCount',
		headerName: 'Conversion Count',
		headerAlign: 'center' as const,
		align: 'center' as const,
		sortable: false,
		editable: true,
		flex: 1,
		valueParser: (value) => Number((value ?? 0).replace(/\D/g, '')), // Remove all non-numbers,
		valueSetter: adaptGridValueCallback(({ value, row }) => ({ ...row, conversionCount: Number(value) })),
	},
	{
		field: 'daysRequiredForNoticeToConvert',
		headerName: 'Notice to Convert (Days)',
		headerAlign: 'center' as const,
		align: 'center' as const,
		sortable: false,
		editable: true,
		flex: 1.5,
		valueFormatter: adaptGridValueCallback(({ value }) => value ?? '-'),
		valueParser: (value) => Number((value ?? 0).replace(/\D/g, '')), // Remove all non-numbers,
		valueSetter: adaptGridValueCallback(({ value, row }) => ({
			...row,
			daysRequiredForNoticeToConvert: Number(value),
		})),
	},
];

export const ConversionRight: FC<ConversionRightProps> = ({
	conversionRight,
	leasingRight,
	refetchLeasingRight,
	countAndRateId,
	handleCancelNewMode,
	editMode,
	setEditMode,
	pageEditInProgress,
	setShowDialog,
	setShowSnackbar,
}) => {
	const gridApiRef = useGridApiRef();

	const [rows, setRows] = useState(conversionRight.history);
	const [rateId, setRateId] = useState<string>(null);
	const [sublotId, setSublotId] = useState<number>(null);
	const [expanded, setExpanded] = useState(true);

	const newMode = useMemo(() => String(conversionRight.id).includes('new'), [conversionRight.id]);

	const columns = useMemo(
		() =>
			getColumns(
				gridApiRef,
				conversionRight,
				leasingRight.facilityTimezone,
				editMode,
				newMode,
				leasingRight.subscriptionHasInvoice,
			),
		[
			conversionRight,
			editMode,
			gridApiRef,
			leasingRight.facilityTimezone,
			leasingRight.subscriptionHasInvoice,
			newMode,
		],
	);

	const allowEdit = editMode || !pageEditInProgress;

	const { data: ratesData } = useQuery(GET_RATES_BY_SUBSCRIPTION, {
		variables: { subscriptionId: leasingRight.subscriptionId },
		context: { apiVersion: 'v2' },
		skip: !newMode,
	});
	const rateOptions = ratesData?.getRatesBySubscription ?? [];

	const { data: sublotsData } = useQuery(GET_SUBLOTS, {
		variables: {
			facilityId: leasingRight.facilityId,
			includePrimary: true,
		},
		context: { apiVersion: 'v2' },
		skip: !newMode,
		onCompleted: (data) => {
			const mainFacility = data?.getSublots?.find((sublot) => sublot.name === 'Main Facility');
			if (mainFacility) {
				setSublotId(mainFacility.id);
			}
		},
	});
	const sublotOptions = sublotsData?.getSublots ?? [];

	const [createConversionRight, { loading: createInProgress }] = useMutation(CREATE_CONVERSION_RIGHT);
	const [updateConversionRight, { loading: updateInProgress }] = useMutation(UPDATE_CONVERSION_RIGHT);
	const [deleteConversionRight, { loading: deleteInProgress }] = useMutation(DELETE_CONVERSION_RIGHT);

	const saveInProgress = createInProgress || updateInProgress;

	useEffect(() => {
		setRows(structuredClone(conversionRight.history));
	}, [conversionRight]);

	/**
	 * This useEffect is needed as a workaround because for some reason
	 * isCellEditable() doesn't get the updated editMode state until clicking on the data grid
	 */
	useEffect(() => {
		if (editMode) {
			gridApiRef.current.setCellFocus(conversionRight.history[0].id, 'startDate');
		}
	}, [conversionRight.history, editMode, gridApiRef]);

	const handleCancel = useCallback(() => {
		stopAllEdits({
			saveChanges: false,
			gridApiRef,
			columns,
		});
		setEditMode(false);

		if (newMode) handleCancelNewMode();
		else setRows(structuredClone(conversionRight.history));
	}, [columns, conversionRight.history, gridApiRef, handleCancelNewMode, newMode, setEditMode]);

	const handleAddRow = useCallback(() => {
		const latestEndDate = getTableLatestEndDate(gridApiRef);

		gridApiRef.current.updateRows([
			{
				id: `new-${uuidv4()}`,
				startDate: latestEndDate.add(1, 'day').format('YYYY-MM-DD'),
				conversionCount: 0,
				daysRequiredForNoticeToConvert: 0,
			},
		]);
	}, [gridApiRef]);

	const handleSave = useCallback(async () => {
		stopAllEdits({
			saveChanges: true,
			gridApiRef,
			columns,
		});

		const dataRows = getGridRows(gridApiRef);
		const updatedHistory = dataRows.map((row) => {
			const updatedRow = structuredClone(row);
			if (String(updatedRow.id).includes('new')) delete updatedRow.id;
			return updatedRow;
		});

		try {
			if (updatedHistory.some((h) => !h.conversionCount)) {
				throw new Error('Conversion count is required');
			}

			updatedHistory.forEach((history) => {
				const dateError = validateConversionRightDates(
					leasingRight.commencementDate,
					leasingRight.expirationDate,
					history.startDate,
				);

				if (dateError) throw new Error(dateError);
			});

			try {
				if (newMode) {
					if (!rateId) throw new Error('Please select a rate');
					if (!sublotId) throw new Error('Please select a sublot');

					await createConversionRight({
						variables: {
							leasingCountAndRateId: countAndRateId,
							rateId,
							sublotId,
							leasingConversionRightHistory: updatedHistory,
							leasingRightId: leasingRight.id,
						},
						context: { apiVersion: 'v2' },
					});
				} else {
					await updateConversionRight({
						variables: {
							id: conversionRight.id,
							leasingCountAndRateId: countAndRateId,
							leasingConversionRightHistory: updatedHistory,
							leasingRightId: leasingRight.id,
						},
						context: { apiVersion: 'v2' },
					});
				}
			} catch (e) {
				console.log(e);
				throw new Error(leasingRightErrorMessage(e));
			}

			setShowSnackbar({
				message: 'Saved successfully',
				severity: 'success',
				autoHideDuration: 5000,
			});

			setEditMode(false);
			await refetchLeasingRight();
		} catch (e) {
			setShowSnackbar({
				message: e.message,
				severity: 'error',
				autoHideDuration: 8000,
			});
		}
	}, [
		columns,
		conversionRight.id,
		countAndRateId,
		createConversionRight,
		gridApiRef,
		leasingRight.commencementDate,
		leasingRight.expirationDate,
		newMode,
		rateId,
		refetchLeasingRight,
		setEditMode,
		setShowSnackbar,
		sublotId,
		updateConversionRight,
		leasingRight.id,
	]);

	const handleDelete = useCallback(async () => {
		setShowDialog(
			<SimpleModal
				content='Are you sure?'
				handleDismiss={() => setShowDialog(null)}
				handleConfirm={async () => {
					setShowDialog(null);

					try {
						await deleteConversionRight({
							variables: {
								id: conversionRight.id,
								leasingRightId: leasingRight.id,
							},
							context: { apiVersion: 'v2' },
						});

						setShowSnackbar({
							message: 'Deleted successfully',
							severity: 'success',
							autoHideDuration: 5000,
						});

						await refetchLeasingRight();
					} catch (e) {
						console.log(e);
						setShowSnackbar({
							message: 'Internal Server Error',
							severity: 'error',
							autoHideDuration: 5000,
						});
					}
				}}
				confirmText='Delete'
				confirmButtonColor='error'
				dismissText='Cancel'
				contentStyle={{
					fontSize: 'large',
					display: 'flex',
					justifyContent: 'center',
					margin: '3em',
					minWidth: '20em',
				}}
			/>,
		);
	}, [
		conversionRight.id,
		deleteConversionRight,
		refetchLeasingRight,
		setShowDialog,
		setShowSnackbar,
		leasingRight.id,
	]);

	const isCellEditable = useCallback(
		({ field, row }: GridCellParams) => {
			if (field === 'endDate') return false;
			if (newMode) return true;
			if (!editMode) return false;

			const { facilityTimezone, subscriptionHasInvoice } = leasingRight;
			const allowRowEdits = getAllowRowEdits(
				row,
				conversionRight.history,
				facilityTimezone,
				newMode,
				subscriptionHasInvoice,
			);

			return allowRowEdits;
		},
		[conversionRight.history, editMode, leasingRight, newMode],
	);

	return (
		<Box key={conversionRight.id}>
			<ClickablePaper
				sx={{ backgroundColor: 'whitesmoke', paddingTop: '1em', paddingBottom: '1em' }}
				disableClick={newMode}
				onClick={() => setExpanded(!expanded)}
			>
				<Box display='flex' alignItems='center' gap='1em' ml='1em'>
					{expanded ? <ExpandLess fontSize='large' /> : <ExpandMore fontSize='large' />}
					{getRateAndSublotFields({
						rateId,
						setRateId,
						sublotId,
						setSublotId,
						rateName: conversionRight.rateName,
						sublotName: conversionRight.sublotName,
						rateOptions,
						sublotOptions,
						newMode,
						gridApiRef,
						grossRatePrice: conversionRight.grossRatePrice,
					})}
				</Box>
			</ClickablePaper>
			<Collapse in={expanded}>
				<Paper
					sx={{
						backgroundColor: 'whitesmoke',
						borderTop: 'solid',
						borderColor: 'lightgray',
						paddingBottom: '0.5em',
					}}
				>
					{getTableButtons({
						newMode,
						leaseNewMode: false,
						editMode,
						setEditMode,
						allowEdit,
						allowDelete: true,
						saveInProgress,
						handleSave,
						handleAddRow,
						handleCancel,
						handleDelete,
						deleteInProgress,
					})}
					<Box
						sx={{
							backgroundColor: 'white',
							margin: '1em',
							...(editMode && {
								'& .MuiDataGrid-cell': { backgroundColor: 'gainsboro' },
								'& .MuiDataGrid-cell--actions': { backgroundColor: 'white' },
								'& .MuiDataGrid-cell--editable': { backgroundColor: 'white' },
							}),
						}}
					>
						<DataGrid
							rows={rows}
							columns={columns}
							apiRef={gridApiRef}
							isCellEditable={isCellEditable}
							columnVisibilityModel={{ actions: editMode }}
							autoHeight
							disableColumnReorder
							disableColumnMenu
							disableRowSelectionOnClick
							hideFooter
						/>
					</Box>
				</Paper>
			</Collapse>
		</Box>
	);
};
